import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'

import { Button } from './Button.tsx'
import { cx } from './cx.ts'

import './form.css'
import './dateTimePicker.css'

/**
 * **날짜 · 시각 고르개** — 단추를 누르면 앱 달력이 펼쳐진다(eric 2026-10-20 「mm/dd/yyyy 이거 없애고.. 달력 누르고 선택하게 해줘. 달력 누르면 색상이..
 * 너무 올드해」). 브라우저 `datetime-local` 대신이다 — 브라우저가 그리는 고르개는 글자 꼴(`mm/dd/yyyy`) · 색 · 다크 테마가 앱과 따로 논다.
 *
 * - 값은 날짜(`YYYY-MM-DD`) · 시각(`HH:MM`) 둘이다. 시각은 비울 수 있다(모르는 시각을 지어 넣지 않는다 — 시 · 분 고르개의 「—」).
 * - 왼쪽 달력에서 날짜, 오른쪽 두 열에서 시 · 분(eric 2026-10-20). 날짜를 누르면 날짜만 바뀐다(시각은 그대로).
 * - 「확인」은 고른 값을 두고 닫는다. 「취소」는 펼치기 전 값으로 되돌리고 닫는다(eric 2026-10-20 「지우기를 취소.. 닫기를 확인으로」).
 * - 바깥 클릭 · Esc 로 닫힌다. 원자는 한글을 만들지 않는다 — 글자는 부르는 쪽이 `labels` 로 준다.
 */
export interface DateTimePickerLabels {
  /** 비었을 때 단추 글자(「날짜 · 시각 선택」) */
  placeholder: string
  prevMonth: string
  nextMonth: string
  /** 「{year}년 {month}월」 */
  monthTitle: (year: number, month: number) => string
  /** 요일 머리 일 · 월 … 토 */
  weekdays: readonly string[]
  hour: string
  minute: string
  /** 시 열 맨 위 「—」의 접근 이름(「시각 없음」) */
  noTime: string
  cancel: string
  confirm: string
}

export interface DateTimePickerProps {
  date: string
  time: string
  onChange: (next: { date: string; time: string }) => void
  labels: DateTimePickerLabels
  /** 오늘(`YYYY-MM-DD`) — 달력에 테두리로 표시 · 비었을 때 처음 보이는 달 */
  today: string
  id?: string | undefined
  name?: string | undefined
  'aria-label'?: string | undefined
  describedBy?: string | undefined
  invalid?: boolean | undefined
  readOnly?: boolean | undefined
}

const pad = (n: number) => String(n).padStart(2, '0')
const ymd = (y: number, m: number, d: number) => `${String(y)}-${pad(m)}-${pad(d)}`

function parse(date: string): { y: number; m: number } | undefined {
  const match = /^(\d{4})-(\d{2})-\d{2}$/u.exec(date)
  return match === null ? undefined : { y: Number(match[1]), m: Number(match[2]) }
}

export function DateTimePicker({
  date,
  time,
  onChange,
  labels,
  today,
  id,
  name,
  'aria-label': ariaLabel,
  describedBy,
  invalid = false,
  readOnly = false,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false)
  const shown = parse(date) ?? parse(today) ?? { y: 2000, m: 1 }
  const [view, setView] = useState(shown)
  /** 펼칠 때의 값 — 「취소」가 이 값으로 되돌린다 */
  const [before, setBefore] = useState({ date, time })
  const rootRef = useRef<HTMLSpanElement | null>(null)
  const popRef = useRef<HTMLDivElement | null>(null)
  const popId = useId()
  const clockRef = useRef<HTMLDivElement | null>(null)
  /* 펼치면 고른 시 · 분이 열 가운데에 보이게 굴린다(브라우저 고르개처럼) */
  useLayoutEffect(() => {
    if (!open) return
    for (const col of clockRef.current?.querySelectorAll<HTMLElement>('.a-dtp__colbody') ?? []) {
      const on = col.querySelector<HTMLElement>('.a-dtp__opt--on')
      if (on !== null) col.scrollTop = on.offsetTop - (col.clientHeight - on.offsetHeight) / 2
    }
  }, [open])
  /*
   * 펼친 달력은 화면 기준(`position: fixed`)으로 단추 바로 아래에 선다 — 구르는 상자(모달 본문 · 폼 칸) 안에서 잘리지 않게. 아래 자리가 모자라면
   * 단추 위로 편다. 창 크기 · 스크롤이 바뀌면 다시 잰다
   */
  const [place, setPlace] = useState<CSSProperties | undefined>(undefined)
  useLayoutEffect(() => {
    if (!open) return
    const measure = () => {
      const trigger = rootRef.current?.getBoundingClientRect()
      const pop = popRef.current?.getBoundingClientRect()
      if (trigger === undefined) return
      const height = pop?.height ?? 0
      const gap = 4
      const below = window.innerHeight - trigger.bottom
      const top =
        below < height + gap && trigger.top > below
          ? trigger.top - height - gap
          : trigger.bottom + gap
      setPlace({ left: trigger.left, top: Math.max(gap, top) })
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const toggle = () => {
    if (readOnly) return
    if (!open) {
      setView(parse(date) ?? parse(today) ?? shown)
      setBefore({ date, time })
    }
    setOpen((v) => !v)
  }
  const move = (delta: number) => {
    const index = view.y * 12 + (view.m - 1) + delta
    setView({ y: Math.floor(index / 12), m: (index % 12) + 1 })
  }

  const first = new Date(view.y, view.m - 1, 1).getDay()
  const days = new Date(view.y, view.m, 0).getDate()
  const cells: (number | null)[] = [
    ...Array.from({ length: first }, () => null),
    ...Array.from({ length: days }, (_, i) => i + 1),
  ]
  const [hh = '', mm = ''] = time === '' ? [] : time.split(':')
  const setTime = (h: string, m: string) =>
    onChange({ date, time: h === '' ? '' : `${h}:${m === '' ? '00' : m}` })

  const text = date === '' ? labels.placeholder : time === '' ? date : `${date} ${time}`

  return (
    <span className="a-dtp" ref={rootRef}>
      <button
        aria-controls={open ? popId : undefined}
        aria-describedby={describedBy}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-invalid={invalid || undefined}
        aria-label={
          ariaLabel === undefined ? undefined : `${ariaLabel} ${date === '' ? '' : text}`.trim()
        }
        className={cx(
          'a-input',
          'a-input--wide',
          'a-dtp__trigger',
          date === '' && 'a-dtp__trigger--empty',
        )}
        name={name}
        disabled={readOnly}
        id={id}
        onClick={toggle}
        type="button"
      >
        <span className="a-dtp__text num">{text}</span>
        <svg aria-hidden="true" className="a-dtp__icon" viewBox="0 0 16 16">
          <rect
            fill="none"
            height="11"
            rx="2"
            stroke="currentColor"
            strokeWidth="1.4"
            width="12"
            x="2"
            y="3"
          />
          <path d="M2 6.5h12M5 1.5v3M11 1.5v3" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      </button>
      {open ? (
        <div
          aria-label={ariaLabel}
          className="a-dtp__pop"
          id={popId}
          ref={popRef}
          role="dialog"
          style={place}
        >
          <div className="a-dtp__body">
            <div className="a-dtp__cal">
              <div className="a-dtp__head">
                <button
                  aria-label={labels.prevMonth}
                  className="a-dtp__nav"
                  onClick={() => move(-1)}
                  type="button"
                >
                  ‹
                </button>
                <span className="a-dtp__month" aria-live="polite">
                  {labels.monthTitle(view.y, view.m)}
                </span>
                <button
                  aria-label={labels.nextMonth}
                  className="a-dtp__nav"
                  onClick={() => move(1)}
                  type="button"
                >
                  ›
                </button>
              </div>
              <div className="a-dtp__grid" role="grid">
                {labels.weekdays.map((w) => (
                  <span className="a-dtp__wd" key={w} role="columnheader">
                    {w}
                  </span>
                ))}
                {cells.map((d, i) =>
                  d === null ? (
                    <span key={`e${String(i)}`} />
                  ) : (
                    <button
                      aria-pressed={ymd(view.y, view.m, d) === date}
                      className={cx(
                        'a-dtp__day',
                        ymd(view.y, view.m, d) === date && 'a-dtp__day--on',
                        ymd(view.y, view.m, d) === today && 'a-dtp__day--today',
                      )}
                      data-date={ymd(view.y, view.m, d)}
                      key={d}
                      onClick={() => onChange({ date: ymd(view.y, view.m, d), time })}
                      type="button"
                    >
                      {d}
                    </button>
                  ),
                )}
              </div>
            </div>
            {/*
             * 오른쪽 시 · 분 목록(eric 2026-10-20 「왼쪽은 달력 오른쪽은... 시간 분 선택하는 거」) — 브라우저 고르개처럼 세로로 구르는 두 열.
             * 시 열 맨 위 「—」는 «시각 없음»(모르는 시각을 지어 넣지 않는다). 시가 없으면 분 열은 잠긴다
             */}
            <div className="a-dtp__clock" ref={clockRef}>
              <TimeColumn
                label={labels.hour}
                none={labels.noTime}
                onPick={(h) => setTime(h, mm)}
                value={hh}
                values={HOURS}
              />
              <TimeColumn
                disabled={hh === ''}
                label={labels.minute}
                onPick={(m) => setTime(hh, m)}
                value={mm}
                values={MINUTES}
              />
            </div>
          </div>
          {/* 바닥 단추는 다른 화면의 「취소」 · 「확인」과 같은 꼴(알약 · 주 버튼 채움 · eric 2026-10-20 「다른 취소 확인 버튼 처럼」) */}
          <div className="a-dtp__foot">
            <Button
              onClick={() => {
                onChange(before)
                setOpen(false)
              }}
              size="pill"
            >
              {labels.cancel}
            </Button>
            <Button onClick={() => setOpen(false)} size="pill" variant="primary">
              {labels.confirm}
            </Button>
          </div>
        </div>
      ) : null}
    </span>
  )
}

const HOURS = Array.from({ length: 24 }, (_, h) => pad(h))
const MINUTES = Array.from({ length: 60 }, (_, m) => pad(m))

/** 시 · 분 한 열 — 세로로 구르는 단추 목록. `none` 이 있으면 맨 위에 «없음»(「—」) 칸 */
function TimeColumn({
  label,
  values,
  value,
  onPick,
  none,
  disabled = false,
}: {
  label: string
  values: readonly string[]
  value: string
  onPick: (value: string) => void
  none?: string | undefined
  disabled?: boolean | undefined
}) {
  return (
    <div aria-label={label} className="a-dtp__col" role="group">
      <span aria-hidden="true" className="a-dtp__colhead">
        {label}
      </span>
      <div className="a-dtp__colbody">
        {none === undefined ? null : (
          <button
            aria-label={none}
            aria-pressed={value === ''}
            className={cx('a-dtp__opt', value === '' && 'a-dtp__opt--on')}
            disabled={disabled}
            onClick={() => onPick('')}
            type="button"
          >
            —
          </button>
        )}
        {values.map((v) => (
          <button
            aria-pressed={v === value}
            className={cx('a-dtp__opt', v === value && 'a-dtp__opt--on')}
            disabled={disabled}
            key={v}
            onClick={() => onPick(v)}
            type="button"
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  )
}
